/**
 * SpeakerRoleAssignment — Toolbar for assigning roles to transcript speakers.
 *
 * Displays detected speakers with dropdown menus to assign roles
 * (Clinician, Patient, Spouse, etc.). Auto-assigns Speaker 1 as
 * Clinician by default. Saves clinician display name preference
 * to user settings for reuse across sessions.
 *
 * Rendered inside TranscriptTray below the header, similar to the
 * quality report panel.
 *
 * See: docs/features/document-system/transcript-improvements.md#part-5
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Users, Check, RotateCcw, UserX } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import { Link } from "react-router-dom";

/**
 * Available speaker role options.
 * "patient" maps to the actual patient name from the document.
 */
const ROLE_OPTIONS = [
  { value: "clinician", label: "Clinician" },
  { value: "patient", label: "Patient Name" },
  { value: "spouse", label: "Spouse / Partner" },
  { value: "parent", label: "Parent" },
  { value: "family", label: "Family Member" },
  { value: "interpreter", label: "Interpreter" },
  { value: "other", label: "Other" },
];

/**
 * Get display label for a role, substituting patient name if needed.
 */
const getRoleDisplayLabel = (roleValue, patientName, clinicianName) => {
  if (roleValue === "patient") {
    return patientName || "Patient";
  }
  if (roleValue === "clinician") {
    return clinicianName || "Clinician";
  }
  const option = ROLE_OPTIONS.find((o) => o.value === roleValue);
  return option?.label || roleValue || "Unknown";
};

/**
 * Build default role assignments (Speaker 1 → Clinician, others → Patient / unassigned).
 */
const buildDefaultAssignments = (speakerCount) => {
  const assignments = {};
  for (let i = 0; i < speakerCount; i++) {
    if (i === 0) {
      assignments[i] = "clinician";
    } else if (i === 1) {
      assignments[i] = "patient";
    } else {
      assignments[i] = "";
    }
  }
  return assignments;
};

const SpeakerRoleAssignment = ({
  speakerCount = 0,
  speakerRoles = {},       // Saved role assignments from Firestore
  patientName = "",
  clinicianDisplayName = "", // From user settings
  userId,
  onRolesChange,            // Callback when roles change (parent saves to Firestore)
  onRolesPreview,           // Callback for real-time transcript preview (fires on every dropdown change)
  onClinicianNameChange,    // Callback when clinician name preference changes
  sampleUtterances = [],    // First utterance per speaker for preview
  hideSpeakerLabels = false, // Per-document toggle to hide speaker labels
  onToggleSpeakerLabels,     // Callback to toggle speaker labels visibility
}) => {
  // Local state for role assignments
  const [assignments, setAssignments] = useState(() => {
    if (speakerRoles && Object.keys(speakerRoles).length > 0) {
      return speakerRoles;
    }
    return buildDefaultAssignments(speakerCount);
  });

  // State for editing clinician name
  // NOTE: Inline clinicianDisplayName edit was removed in favor of the
  // Account page (firstName/lastName/degree → derived display). The
  // `clinicianDisplayName` prop is still honored for backward compat with
  // callers that compute it server-side or from legacy user settings.

  // Dirty tracking — have assignments changed from saved version?
  const isDirty = useMemo(() => {
    if (!speakerRoles || Object.keys(speakerRoles).length === 0) {
      // If no saved roles, dirty if any assignment differs from default
      const defaults = buildDefaultAssignments(speakerCount);
      return JSON.stringify(assignments) !== JSON.stringify(defaults);
    }
    return JSON.stringify(assignments) !== JSON.stringify(speakerRoles);
  }, [assignments, speakerRoles, speakerCount]);

  // Re-sync when saved roles change (e.g., reload)
  useEffect(() => {
    if (speakerRoles && Object.keys(speakerRoles).length > 0) {
      setAssignments(speakerRoles);
    } else if (speakerCount > 0) {
      setAssignments(buildDefaultAssignments(speakerCount));
    }
  }, [speakerRoles, speakerCount]);

  // Update local clinician name when prop changes
  useEffect(() => {
    // kept as a hook point; prop is now read-only in this component
  }, [clinicianDisplayName]);

  // Handle role dropdown change
  const handleRoleChange = useCallback((speakerIndex, newRole) => {
    const updated = { ...assignments, [speakerIndex]: newRole };
    setAssignments(updated);
    // Notify parent for real-time transcript preview (outside the updater to
    // avoid "setState during render" in strict/concurrent mode).
    if (onRolesPreview) {
      onRolesPreview(updated);
    }
  }, [assignments, onRolesPreview]);

  // Save roles to parent (Firestore)
  const handleSaveRoles = useCallback(() => {
    if (onRolesChange) {
      onRolesChange(assignments);
    }
  }, [assignments, onRolesChange]);

  // Reset to auto-assigned defaults
  const handleReset = useCallback(() => {
    const defaults = buildDefaultAssignments(speakerCount);
    setAssignments(defaults);
    // Update transcript preview immediately
    if (onRolesPreview) {
      onRolesPreview(defaults);
    }
  }, [speakerCount, onRolesPreview]);

  // Save clinician name preference — removed. Display name is now managed
  // in the Account page (users/{uid}.firstName + lastName + degree).
  // `onClinicianNameChange` and `userId` props are retained for backward
  // compatibility with call sites but are no longer wired to an edit UI here.

  // Get preview text for a speaker
  const getSpeakerPreview = useCallback((speakerIndex) => {
    const utterance = sampleUtterances.find((u) => u.speaker === speakerIndex);
    if (!utterance?.transcript) return null;
    const text = utterance.transcript;
    return text.length > 60 ? text.slice(0, 60) + "…" : text;
  }, [sampleUtterances]);

  if (speakerCount === 0) {
    return null;
  }

  return (
    <div className="speaker-role-panel">
      {/* Header row */}
      <div className="speaker-role-header">
        <div className="speaker-role-title">
          <Users size={14} />
          <span>Speaker Roles</span>
        </div>
        <div className="speaker-role-actions">
          {speakerCount > 1 && onToggleSpeakerLabels && (
            <Tooltip title={hideSpeakerLabels ? "Show speaker labels" : "Hide speaker labels"} arrow>
              <button
                type="button"
                className={`speaker-role-btn${hideSpeakerLabels ? " speaker-role-btn-active" : ""}`}
                onClick={onToggleSpeakerLabels}
                aria-label={hideSpeakerLabels ? "Show speaker labels" : "Hide speaker labels"}
              >
                <UserX size={14} />
              </button>
            </Tooltip>
          )}
          <Tooltip title="Reset to auto-assigned" arrow>
            <button
              type="button"
              className="speaker-role-btn"
              onClick={handleReset}
              aria-label="Reset roles"
            >
              <RotateCcw size={14} />
            </button>
          </Tooltip>
          {isDirty && (
            <Tooltip title="Save role assignments" arrow>
              <button
                type="button"
                className="speaker-role-btn speaker-role-save-btn"
                onClick={handleSaveRoles}
                aria-label="Save roles"
              >
                <Check size={14} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Clinician name (read-only) — edit in Account page */}
      <div className="speaker-role-clinician-row">
        <span className="speaker-role-clinician-label">Your Display Name:</span>
        <span className="speaker-role-clinician-value" style={{ cursor: "default" }}>
          {clinicianDisplayName || "Clinician"}
        </span>
        <Link
          to="/account"
          style={{ marginLeft: "8px", fontSize: "var(--text-xs)", color: "var(--color-link, #2563eb)" }}
          title="Edit in Account"
        >
          Edit in Account
        </Link>
      </div>

      {/* Speaker list */}
      <div className="speaker-role-list">
        {Array.from({ length: speakerCount }, (_, i) => {
          const preview = getSpeakerPreview(i);
          const currentRole = assignments[i] || "";

          return (
            <div key={i} className="speaker-role-row">
              <div className="speaker-role-select-wrapper">
                <span className="speaker-role-index">Speaker {i + 1}:</span>
                <select
                  className="speaker-role-select"
                  value={currentRole}
                  onChange={(e) => handleRoleChange(i, e.target.value)}
                  aria-label={`Role for Speaker ${i + 1}`}
                >
                  <option value="">Unassigned</option>
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.value === "patient" && patientName
                        ? patientName
                        : opt.value === "clinician" && clinicianDisplayName
                        ? clinicianDisplayName
                        : opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {preview && (
                <div className="speaker-role-preview">"{preview}"</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { ROLE_OPTIONS, getRoleDisplayLabel, buildDefaultAssignments };
export default React.memo(SpeakerRoleAssignment);
